/**
 * The two hooks components actually use, and neither mentions a key.
 *
 *   `useCommandHandler(id, fn)` — "here is what this command does, while I am
 *   mounted". The chord that invokes it is the keymap's business.
 *
 *   `useCommandScope(scope)` — "this surface is on screen", which is what makes
 *   scoped bindings live.
 *
 * Both read `useCommandsApi`, never `useCommandsState` — see the comment on
 * `CommandsApi` for why that distinction is load-bearing rather than tidy.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useCommandsApi, type CommandHandler } from './CommandsProvider';
import type { CommandId, CommandScope } from './types';

export interface UseCommandHandlerOptions {
  /**
   * Whether the command can run right now. Defaults to true.
   *
   * This is the "nothing to save" / "no row selected" case: the binding stays
   * registered and visible in the shortcuts panel, marked unavailable, rather
   * than disappearing. A shortcut that vanishes reads as broken; one that is
   * listed and greyed reads as conditional.
   */
  readonly enabled?: boolean;
}

/**
 * Supply the behaviour for one command while this component is mounted.
 *
 * The handler is re-registered on every change, and read at fire time rather
 * than bind time, so a closure over fresh state is always what runs — no stale
 * `count`, no dependency list to get wrong.
 *
 * Handlers STACK per command: the innermost mounted claimant wins, and the
 * command is handed back to the outer one when it unmounts. Two mounted
 * claimants is logged, because whichever is not on top is silently inert and
 * that is rarely intended.
 */
export function useCommandHandler(
  id: CommandId,
  handler: CommandHandler,
  options: UseCommandHandlerOptions = {},
): void {
  const { registerHandler } = useCommandsApi();
  const enabled = options.enabled ?? true;

  // The canonical latest-ref pattern, and the write is in an EFFECT rather
  // than in the render body on purpose: `react-hooks/refs` rejects a render
  // -phase ref write, because the value a render observed and the value React
  // eventually paints can then disagree. No dependency array — it has to run
  // after every render, since the whole job is tracking the newest closure.
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });

  // Stable for the component's whole life, so re-rendering with a fresh inline
  // closure does not churn the provider's registration.
  const stable = useCallback<CommandHandler>(
    (event) => latest.current(event),
    [],
  );

  useEffect(
    () => registerHandler(id, { handler: stable, enabled }),
    [id, enabled, registerHandler, stable],
  );
}

/**
 * Mark a scope active while this component is mounted.
 *
 * Call it once, at the top of the surface that owns the scope — the mesh
 * viewer, the design-points table, the report editor. Every command declaring
 * that scope in the catalog goes live, and its bindings take precedence over
 * global ones on the same chord for as long as the surface is mounted.
 *
 * Nesting is fine: two boundaries for one scope pop independently, and the
 * outer one keeps the scope active after the inner unmounts.
 */
export function useCommandScope(scope: CommandScope): void {
  const { pushScope } = useCommandsApi();
  useEffect(() => pushScope(scope), [scope, pushScope]);
}
